import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ProductDetailsPageContext } from "@insite/content-library/Pages/ProductDetailsPage";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Hidden from "@insite/mobius/Hidden";
import Icon, { IconProps } from "@insite/mobius/Icon";
import ListItem, { UnorderedList } from "@insite/mobius/Lists";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { css } from "styled-components";

import AutoIcon from "@husky-hrc/Components/Icons/AutoIcon";
import ConsumerPackagingIcon from "@husky-hrc/Components/Icons/ConsumerPackagingIcon";
import ElectronicsIcon from "@husky-hrc/Components/Icons/ElectronicsIcon";
import MedicalIcon from "@husky-hrc/Components/Icons/MedicalIcon";

type Props = WidgetProps & HasProduct;

export interface ProductDetailsAttributesStyles {
    wrapper?: InjectableCss;
    marketSegmentList?: InjectableCss;
    marketSegmentListTitle?: InjectableCss;
    marketSegmentListItem?: InjectableCss;
    marketSegmentListItemTitleContainer?: InjectableCss;
    marketSegmentListItemTitleContainerMobile?: InjectableCss;
    marketSegmentListItemTitle?: TypographyPresentationProps;
    iconStyles?: IconProps;
    iconStylesMobile?: IconProps;
    attributeItem: InjectableCss;
    attributeTypeLabelText?: TypographyPresentationProps;
    attributeTypeValuesText?: TypographyPresentationProps;
}

export const attributesStyles: ProductDetailsAttributesStyles = {
    wrapper: {
        css: css`
            padding-bottom: 15px;
        `,
    },
    attributeItem: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            display: flex;
                            flex-direction: column;
                            align-items: center;
                            margin-top: 30px;
                        `,
                        null,
                        null,
                        css`
                            margin-top: 112px;
                        `,
                        null,
                    ],
                    "min",
                )}
        `,
    },
    attributeTypeLabelText: {
        forwardAs: "h4",
        variant: "h4",
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            margin-bottom: 40px;
                            text-align: center;
                            font-size: 24px;
                        `,
                        null,
                        css`
                            font-size: 24px;
                        `,
                        null,
                    ],
                    "min",
                )}
        `,
    },
    marketSegmentList: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            list-style: none;
                            display: flex;
                            width: 100%;
                            padding-left: 0px;
                            flex-wrap: wrap;
                            min-height: 100%;
                            margin-top: 0;
                            margin-bottom: 20px;
                        `,
                        null,
                        null,
                        css`
                            justify-content: center;
                            flex-wrap: unset;
                            padding-left: 0px;
                            gap: 25px;
                        `,
                        null,
                    ],
                    "min",
                )}
        `,
    },
    marketSegmentListTitle: {
        css: css`
            text-align: center;
        `,
    },
    marketSegmentListItemTitleContainer: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            text-align: center;
                            max-width: 90%;
                        `,
                        null,
                        null,
                        null,
                        css`
                            max-width: none;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    marketSegmentListItemTitle: {
        forwardAs: "h5",
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            font-size: 16px;
                            letter-spacing: 0;
                            line-height: 24px;
                            font-weight: 900;
                        `,
                        null,
                        null,
                        css`
                            font-size: 18px;
                            line-height: 22px;
                            letter-spacing: 2px;
                        `,
                        null,
                    ],
                    "min",
                )}
        `,
    },
    marketSegmentListItem: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            display: flex;
                            flex-direction: column;
                            align-items: center;
                            max-width: 385px;

                            width: 50%;
                            margin-bottom: 40px;
                            :last-of-type,
                            :nth-last-of-type(2):nth-of-type(odd) {
                                margin-bottom: 0;
                            }
                        `,
                        null,
                        null,
                        css`
                            margin-bottom: 125px;
                            padding: 20px 35px;
                        `,
                        css`
                            padding: 20px 75px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    iconStyles: {
        size: 150,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            display: none;
                        `,
                        null,
                        null,
                        css`
                            display: block;
                            margin-bottom: 30px;
                        `,
                        null,
                    ],
                    "min",
                )}
        `,
    },
    iconStylesMobile: {
        size: 74.5,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            margin-bottom: 30px;
                        `,
                        null,
                        null,
                        css`
                            display: none;
                        `,
                        null,
                    ],
                    "min",
                )}
        `,
    },
    attributeTypeValuesText: {
        css: css`
            margin-bottom: 5px;
        `,
    },
};

const styles = attributesStyles;

const GetIcon = (attribute: any) => {
    switch (attribute) {
        case "Consumer Packaging":
            return ConsumerPackagingIcon;
        case "Auto":
            return AutoIcon;
        case "Medical":
            return MedicalIcon;
        case "Electronics":
            return ElectronicsIcon;
        default:
            return AutoIcon;
    }
};
const MarketSegment: React.FC<Props> = ({ product: { brand, attributeTypes } }) => {
    if (!brand && (!attributeTypes || attributeTypes.length === 0)) {
        return null;
    }

    const limitedAttributeTypes = (attributeTypes ?? []).filter(attribute => {
        return attribute.name === "Market Segment";
    });

    return (
        <StyledWrapper {...styles.wrapper} data-test-selector="productDetails_attributes">
            {limitedAttributeTypes.map(attributeType => (
                <StyledWrapper
                    key={attributeType.id.toString()}
                    data-test-selector="attributes_item"
                    data-attributetypeid={attributeType.id}
                    {...styles.attributeItem}
                >
                    <StyledWrapper {...styles.marketSegmentListItemTitleContainer}>
                        <Typography data-test-selector="attributes_item_label" {...styles.attributeTypeLabelText}>
                            {translate("MARKETS SERVED")}
                        </Typography>
                    </StyledWrapper>
                    <UnorderedList {...styles.marketSegmentList}>
                        {(attributeType.attributeValues ?? []).map(attributeValue => (
                            <ListItem key={attributeValue.id} {...styles.marketSegmentListItem}>
                                <Icon {...{ ...styles.iconStyles, src: GetIcon(attributeValue.value) }} />
                                <Icon {...{ ...styles.iconStylesMobile, src: GetIcon(attributeValue.value) }} />
                                <StyledWrapper {...styles.marketSegmentListItemTitleContainer}>
                                    <Typography {...styles.marketSegmentListItemTitle}>
                                        {attributeValue.valueDisplay.toUpperCase()}
                                    </Typography>
                                </StyledWrapper>
                            </ListItem>
                        ))}
                    </UnorderedList>
                </StyledWrapper>
            ))}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(MarketSegment),
    definition: {
        displayName: "Market Segment",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
